import { useEffect, useState } from 'react'
import { useAuth } from '../auth'
import cdxLogo from '../assets/cdx.png'
import AssinaturaCdxModal from './AssinaturaCdxModal'

/**
 * Card "Serviço Cloud CDX VIP" no topo — SÓ administrador (`isAdminReal`: o
 * admin real, inclusive enquanto simula outro usuário no "Ver como"). A trava é
 * daqui: o card consulta o CDX Adm direto, sem passar pelo backend da plataforma.
 *
 * Verde = em dia · âmbar = vencendo em até 5 dias · vermelho = atrasado.
 * A cor e o texto vêm do CDX Adm (endpoint único de todos os sistemas da CDX),
 * onde a regra é coberta por teste — aqui só se desenha. Visual espelha os cards do
 * header (.tc-hcard), que o index.css já trata nos dois temas.
 *
 * Clicar abre o modal com os títulos em aberto (com link de pagamento) e os
 * pagos. Quando há parcela vencida, abre uma vez por sessão o aviso de risco de
 * suspensão (regra geral da CDX).
 *
 * Nunca atrapalha a plataforma: se a consulta falhar, ou se a chave for
 * revogada, o card simplesmente não aparece.
 *
 * Consulta: ao montar, a cada 5 min, e a cada 10 s enquanto o card está aberto.
 * Cada consulta leva o motivo, que no CDX Adm vira o relatório de uso do card.
 *
 * Import ESTÁTICO do modal de propósito: um lazy() sem ErrorBoundary derruba a
 * tela inteira quando o chunk some depois de um deploy.
 */

/** Consulta DIRETA ao CDX Adm. A chave é publicável: só abre o resumo de
 *  assinatura desta empresa, só leitura (no CDX Adm fica só o hash dela).
 *  GET simples, sem cabeçalho próprio — não depende de preflight CORS.
 *  motivo: carga = topo montou · fundo = 5 min · poll = 10 s com o card aberto ·
 *  abrir / atualizar / aviso / pagar = ação do usuário. */
const CDX_ADM_ASSINATURA = 'https://adm.cdxsistemas.com.br/api/v1/publico/assinatura'
const CHAVE_CARD = 'cdxpub_d9Ws6vLqcJKXPDsRo-PG9G-VBLe7laGZ'
const consultarCdxAdm = async (motivo = 'carga') => {
  const r = await fetch(
    `${CDX_ADM_ASSINATURA}?chave=${encodeURIComponent(CHAVE_CARD)}&motivo=${motivo}`,
    { cache: 'no-store', keepalive: motivo === 'pagar' },
  )
  if (!r.ok) throw new Error(`CDX Adm respondeu ${r.status}`)
  return r.json()
}

/** Marca que o aviso de atraso já apareceu nesta sessão do navegador.
 *  sessionStorage pode lançar (aba anônima, cookies bloqueados) — nunca deixa
 *  isso derrubar o topo; no pior caso o aviso só aparece de novo. */
const CHAVE_AVISO = 'cdx_assinatura_aviso_atraso'
const jaAvisou = () => { try { return sessionStorage.getItem(CHAVE_AVISO) === '1' } catch { return false } }
const marcarAvisado = () => { try { sessionStorage.setItem(CHAVE_AVISO, '1') } catch { /* paciência */ } }

/** Consultas podem voltar fora de ordem (polling de 10 s + "Atualizar").
 *  Regra: nunca substituir o que está na tela por dado mais VELHO. */
const maisNovo = (novo, atual) => {
  if (!atual?.atualizadoEm || !novo?.atualizadoEm) return true
  return novo.atualizadoEm >= atual.atualizadoEm
}

/* `ring` e NAO `border`: o .tc-hcard do index.css e definido DEPOIS do
   @tailwind utilities e fixa `border: 1px solid #ffb870` (com !important no
   tema escuro), entao qualquer border-* aqui seria ignorada. Ring nao conflita. */
const ANEL = {
  em_dia: 'ring-2 ring-emerald-500',
  a_vencer: 'ring-2 ring-amber-500',
  atrasado: 'ring-2 ring-red-500',
}

/* O ponto colorido carrega a cor (fundo, legivel nos dois temas); o texto fica
   em text-graphite, que o index.css ja clareia sob [data-theme="dark"]. */
const PONTO = {
  em_dia: 'bg-emerald-500',
  a_vencer: 'bg-amber-500',
  atrasado: 'bg-red-500',
}

export default function AssinaturaCdxCard() {
  const { isAdminReal } = useAuth()
  const [resumo, setResumo] = useState(null)
  const [aberto, setAberto] = useState(false)
  const [avisando, setAvisando] = useState(false)
  const [atualizando, setAtualizando] = useState(false)

  useEffect(() => {
    if (!isAdminReal) return undefined
    let vivo = true
    const buscar = async (motivo) => {
      try {
        const data = await consultarCdxAdm(motivo)
        if (!vivo) return
        setResumo((atual) => (maisNovo(data, atual) ? data : atual))
        if (data.situacao === 'atrasado' && !jaAvisou()) {
          marcarAvisado()
          setAvisando(true)
          consultarCdxAdm('aviso').catch(() => undefined)
        }
      } catch {
        /* silencioso — o card some, a plataforma segue normal */
      }
    }
    buscar('carga')
    // 5 min com o topo aberto: a cor do card acompanha o pagamento
    const t = setInterval(() => buscar('fundo'), 5 * 60 * 1000)
    return () => { vivo = false; clearInterval(t) }
  }, [isAdminReal])

  /* Card aberto: avisa o CDX Adm (abrir) e atualiza de 10 em 10 s enquanto
     estiver aberto — o cliente paga e vê a baixa sem clicar em nada. */
  useEffect(() => {
    if (!aberto) return undefined
    let vivo = true
    const consultar = (motivo) => consultarCdxAdm(motivo)
      .then((data) => { if (vivo) setResumo((atual) => (maisNovo(data, atual) ? data : atual)) })
      .catch(() => undefined) // mantém o que já estava na tela
    consultar('abrir')
    const t = setInterval(() => { if (!document.hidden) consultar('poll') }, 10 * 1000)
    return () => { vivo = false; clearInterval(t) }
  }, [aberto])

  /* Clique em "Pagar" (link do Asaas) com o card ou o aviso abertos: só registra
     no CDX Adm — o link abre normalmente. */
  useEffect(() => {
    if (!aberto && !avisando) return undefined
    const aoClicar = (e) => {
      const link = e.target?.closest?.('a[href]')
      if (link && /asaas\.com/i.test(link.href)) consultarCdxAdm('pagar').catch(() => undefined)
    }
    document.addEventListener('click', aoClicar, true)
    return () => document.removeEventListener('click', aoClicar, true)
  }, [aberto, avisando])

  const atualizarAgora = async () => {
    setAtualizando(true)
    try {
      setResumo(await consultarCdxAdm('atualizar'))
    } catch {
      /* mantém o que já estava na tela */
    } finally {
      setAtualizando(false)
    }
  }

  if (!isAdminReal || !resumo || resumo.situacao === 'indisponivel') return null

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        title={`Serviço Cloud CDX VIP — ${resumo.rotulo}`}
        aria-label={`Assinatura CDX: ${resumo.rotulo}. Abrir detalhes`}
        className={`flex flex-shrink-0 items-center gap-2 px-2.5 py-2 rounded-xl transition-colors tc-hcard ${ANEL[resumo.situacao] || ''}`}
      >
        <span className="w-7 h-7 flex-shrink-0 rounded-lg bg-white border border-accent-200 flex items-center justify-center p-0.5">
          <img src={cdxLogo} alt="CDX" className="w-full h-full object-contain" />
        </span>
        <span className="text-left min-w-0 hidden sm:block">
          <span className="block text-[10px] uppercase font-bold tracking-wide text-gray-500 leading-tight">
            Serviço Cloud CDX VIP
          </span>
          <span className="flex items-center gap-1.5 text-xs font-extrabold leading-tight text-graphite">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PONTO[resumo.situacao] || ''}`} />
            {resumo.rotulo}
          </span>
        </span>
      </button>

      {(avisando || aberto) && (
        <AssinaturaCdxModal
          resumo={resumo}
          modoAviso={avisando && !aberto}
          onFechar={() => { setAvisando(false); setAberto(false) }}
          onAtualizar={atualizarAgora}
          atualizando={atualizando}
        />
      )}
    </>
  )
}
